/* eslint-disable max-len */
import cn from 'classnames';
import {
  Field, FieldProps, Form, FormikProvider, useFormik,
} from 'formik';
import { memo, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  requestPostVacancyRecommendation,
  resetErrors,
} from '../../redux/actions/vacancy-page';
import { IDocumentsReducerState } from '../../redux/reducers/documentsReducer';
import { RootState } from '../../redux/reducers/rootReducer';
import { PERSONAL_DATA_POLICY_LINK } from '../../utils/constants/client';
import mediaQueries from '../../utils/media-query/media-query-constants';
import useMediaQuery from '../../utils/media-query/media-query-hook';
import { onMaskPhoneNumber } from '../../utils/on-mask-phone-number';
import { RecommendSchema } from '../../utils/validation/validation';
import AttachFile from '../AttachFile';
import { CloseWarningModal } from '../CloseWarningModal';
import CustomModal from '../CustomModal';
import FormCheckbox from '../FormCheckbox';
import RecommendRoleModal, { RoleVariant } from '../RecommendRoleModal';
import { ResultModal } from '../ResultModal';
import HeartIcon from '../SVGIcons/HeartIcon';
import SadIcon from '../SVGIcons/SadIcon';
import classes from './recommend-modal.module.scss';

interface IRecommendModalProps {
  vacancy: string | null;
  title: string;
  onCloseModal: () => void;
  isOpen: boolean;
  setIsOpen: (isOen: boolean) => void;
  defaultValues: {
    recommenderLastName: string;
    recommenderFirstName: string;
    recommenderPatronymic: string;
    cvFile: File | null;
    recommenderPhoneNumber: string;
    recommenderEmail: string;
    checkPersonalData: boolean;
    checkIT: boolean;
    recommendedLastName: string;
    recommendedFirstName: string;
    recommendedPatronymic: string;
    recommendedPhoneNumber: string;
    recommendedEmail: string;
    concentToBeRecommended: boolean;
  };
}

export interface IRecommendFormValues {
  recommenderLastName: string;
  recommenderFirstName: string;
  recommenderPatronymic: string;
  cvFile: File | null;
  recommenderPhoneNumber: string;
  recommenderEmail: string;
  checkPersonalData: boolean;
  checkIT: boolean;
  recommendedLastName: string;
  recommendedFirstName: string;
  recommendedPatronymic: string;
  recommendedPhoneNumber: string;
  recommendedEmail: string;
  concentToBeRecommended: boolean;
  vacancy: string | null;
}

function RecommendModal({
  vacancy,
  title,
  onCloseModal,
  defaultValues,
  isOpen,
  setIsOpen,
}: IRecommendModalProps) {
  const isLoading = useSelector<RootState, boolean>(
    (state) => state.vacancyReducer.isSending,
  );
  const recommendationError = useSelector<RootState, string | undefined>(
    (state) => state.vacancyReducer.recommendationError,
  );
  const recommendationSent = useSelector<RootState, boolean>(
    (state) => state.vacancyReducer.recommendationSent,
  );
  const docs = useSelector<RootState, IDocumentsReducerState>(
    (state) => state.documentsReducer,
  );
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false);

  const isMobile = useMediaQuery(mediaQueries.mobile);

  const dispatch = useDispatch();
  const initialValues: IRecommendFormValues = {
    ...defaultValues,
    vacancy,
  };

  const resultModalTitle = useMemo(() => {
    if (recommendationError) {
      return 'Рекомендация не отправлена,\nпопробуйте снова';
    }
    return 'Спасибо за рекомендацию!';
  }, [recommendationError]);

  const resultModalSubtitle = useMemo(() => {
    if (recommendationError) {
      return '';
    }
    return 'Твой друг получит письмо со ссылкой на вакансию.\nВ случае успешного взаимодействия с кандидатом, мы вернемся к тебе по дальнейшим шагам.';
  }, [recommendationError]);

  const resultModalIcon = useMemo(() => {
    if (recommendationError) {
      return SadIcon;
    }
    return HeartIcon;
  }, [recommendationError]);

  const resultModalButtonText = useMemo(() => {
    if (recommendationError) {
      return 'Вернуться к форме';
    }
    return '';
  }, [recommendationError]);

  const onRoleSelect = (role: RoleVariant) => {
    if (role === 'EMPLOYEE') {
      window.open(
        'https://jira.sbmt.io/servicedesk/customer/portal/21/group/52',
      );
    } else {
      setIsRecommendModalOpen(true);
    }
    setIsOpen(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: RecommendSchema,
    onSubmit(values: IRecommendFormValues, { resetForm }) {
      dispatch(requestPostVacancyRecommendation({
        values: {
          ...values,
          vacancy,
        },
        resetForm,
      }));
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = formik;

  const onChangeRecommendedPhoneNumber = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setFieldValue('recommendedPhoneNumber', onMaskPhoneNumber(value));
  };

  const onChangeRecommenderPhoneNumber = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setFieldValue('recommenderPhoneNumber', onMaskPhoneNumber(value));
  };

  const handleCloseModal = () => {
    if (recommendationError || recommendationSent) {
      formik.resetForm();
      dispatch(resetErrors());
      setIsRecommendModalOpen(false);
      return;
    }
    if (Object.keys(touched).length === 0) {
      setIsRecommendModalOpen(false);
      return;
    }
    if (isRecommendModalOpen) {
      setIsRecommendModalOpen(false);
      setIsCloseModalOpen(true);
      return;
    }
    setIsCloseModalOpen(true);
  };

  return (
    <>
      <RecommendRoleModal
        isOpen={isOpen}
        closeModal={onCloseModal}
        onSelect={onRoleSelect}
      />

      <ResultModal
        isOpen={!!recommendationError || recommendationSent}
        text={resultModalTitle}
        icon={resultModalIcon}
        subtext={resultModalSubtitle}
        buttonText={resultModalButtonText}
        onButtonClick={() => dispatch(resetErrors())}
        onClose={handleCloseModal}
      />

      <CloseWarningModal
        isOpen={isCloseModalOpen}
        onCancel={() => {
          setIsRecommendModalOpen(true);
          setIsCloseModalOpen(false);
        }}
        onOk={() => {
          formik.resetForm();
          setIsCloseModalOpen(false);
        }}
      />

      <FormikProvider value={formik}>
        <CustomModal.Root
          isOpen={isRecommendModalOpen && !recommendationError && !recommendationSent}
          onClose={handleCloseModal}
          Paper={Form}
          paperClassName={classes['recommend-form']}
          paperProps={{
            onSubmit: handleSubmit,
          }}
        >
          <CustomModal.Header>
            <CustomModal.HeaderText>
              Порекомендовать друга
            </CustomModal.HeaderText>
          </CustomModal.Header>
          <CustomModal.Body>
            <div className={classes['block-heading-text']}>О тебе</div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Фамилия*"
                  type="text"
                  name="recommenderLastName"
                  value={values.recommenderLastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommenderLastName
                        && touched.recommenderLastName,
                    },
                  )}
                />
                {errors.recommenderLastName && touched.recommenderLastName && (
                <div className={classes['recommend-form__error-label']}>
                  {errors.recommenderLastName}
                </div>
                )}
              </div>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Имя*"
                  type="text"
                  name="recommenderFirstName"
                  value={values.recommenderFirstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommenderFirstName
                        && touched.recommenderFirstName,
                    },
                  )}
                />
                {errors.recommenderFirstName
                  && touched.recommenderFirstName && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommenderFirstName}
                    </div>
                )}
              </div>
            </div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Отчество"
                  type="text"
                  name="recommenderPatronymic"
                  value={values.recommenderPatronymic}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommenderPatronymic
                        && touched.recommenderPatronymic,
                    },
                  )}
                />
                {errors.recommenderPatronymic
                  && touched.recommenderPatronymic && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommenderPatronymic}
                    </div>
                )}
              </div>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                  classes['recommend-form__offset-item--desktop-only'],
                )}
              />
            </div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                )}
              >
                <Field
                  placeholder="Мобильный телефон*"
                  type="text"
                  name="recommenderPhoneNumber"
                  value={values.recommenderPhoneNumber}
                  onChange={onChangeRecommenderPhoneNumber}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.recommenderPhoneNumber
                        && touched.recommenderPhoneNumber,
                    },
                  )}
                />
                {values.recommenderPhoneNumber && (
                  <div className={classes.mask}>
                    <span className={classes.mask__filled}>
                      {values.recommenderPhoneNumber}
                    </span>
                    {' '}
                    {'+7 (999) 000 00 00'.slice(
                      values.recommenderPhoneNumber.length,
                    )}
                  </div>
                )}

                {errors.recommenderPhoneNumber
                  && touched.recommenderPhoneNumber && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommenderPhoneNumber}
                    </div>
                )}
              </div>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                )}
              >
                <Field
                  placeholder="Email*"
                  type="recommenderEmail"
                  name="recommenderEmail"
                  value={values.recommenderEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.recommenderEmail && touched.recommenderEmail,
                    },
                  )}
                />
                {errors.recommenderEmail && touched.recommenderEmail && (
                <div className={classes['recommend-form__error-label']}>
                  {errors.recommenderEmail}
                </div>
                )}
              </div>
            </div>

            <div
              className={cn(
                classes['block-heading-text'],
                classes['recommend-form__about-friend'],
              )}
            >
              О друге
            </div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Фамилия*"
                  type="text"
                  name="recommendedLastName"
                  value={values.recommendedLastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommendedLastName
                        && touched.recommendedLastName,
                    },
                  )}
                />
                {errors.recommendedLastName && touched.recommendedLastName && (
                <div className={classes['recommend-form__error-label']}>
                  {errors.recommendedLastName}
                </div>
                )}
              </div>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Имя*"
                  type="text"
                  name="recommendedFirstName"
                  value={values.recommendedFirstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommendedFirstName
                        && touched.recommendedFirstName,
                    },
                  )}
                />
                {errors.recommendedFirstName
                  && touched.recommendedFirstName && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommendedFirstName}
                    </div>
                )}
              </div>
            </div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div className={cn(classes['recommend-form__horizontal-grow'])}>
                <Field
                  placeholder="Отчество"
                  type="text"
                  name="recommendedPatronymic"
                  value={values.recommendedPatronymic}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    classes['recommend-form__offset-item'],
                    {
                      [classes['input--error']]:
                        errors.recommendedPatronymic
                        && touched.recommendedPatronymic,
                    },
                  )}
                />
                {errors.recommendedPatronymic
                  && touched.recommendedPatronymic && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommendedPatronymic}
                    </div>
                )}
              </div>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                  classes['recommend-form__offset-item--desktop-only'],
                )}
              />
            </div>

            <div className={classes['recommend-form__horizontal-wrap']}>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                )}
              >
                <Field
                  placeholder="Мобильный телефон*"
                  type="text"
                  name="recommendedPhoneNumber"
                  value={values.recommendedPhoneNumber}
                  onChange={onChangeRecommendedPhoneNumber}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.recommendedPhoneNumber
                        && touched.recommendedPhoneNumber,
                    },
                  )}
                />
                {values.recommendedPhoneNumber && (
                <div className={classes.mask}>
                  <span className={classes.mask__filled}>
                    {values.recommendedPhoneNumber}
                  </span>
                  {'+7 (999) 000 00 00'.slice(
                    values.recommendedPhoneNumber.length,
                  )}
                </div>
                )}
                {errors.recommendedPhoneNumber
                  && touched.recommendedPhoneNumber && (
                    <div className={classes['recommend-form__error-label']}>
                      {errors.recommendedPhoneNumber}
                    </div>
                )}
              </div>
              <div
                className={cn(
                  classes['recommend-form__horizontal-grow'],
                  classes['recommend-form__offset-item'],
                )}
              >
                <Field
                  placeholder="Email*"
                  type="recommendedEmail"
                  name="recommendedEmail"
                  value={values.recommendedEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={cn(
                    classes.input,
                    {
                      [classes['input--error']]:
                        errors.recommendedEmail && touched.recommendedEmail,
                    },
                  )}
                />
                {errors.recommendedEmail && touched.recommendedEmail && (
                <div className={classes['recommend-form__error-label']}>
                  {errors.recommendedEmail}
                </div>
                )}
              </div>
            </div>

            <Field
              className={classes['recommend-form__offset-item']}
              name="cvFile"
              as={AttachFile}
              allowedFormats="PDF или WORD"
              maxSize="Не более 24МБ"
              required
              value={values.cvFile}
              onChange={(value: null | File) => {
                void setFieldTouched('cvFile', true, true).then(
                  () => void setFieldValue('cvFile', value, true),
                );
              }}
            />
            {touched.cvFile && errors.cvFile && (
              <div className={classes['recommend-form__error-label']}>
                {errors.cvFile}
              </div>
            )}

            <div
              className={cn(
                classes['vacancy-info'],
                classes['recommend-form__offset-item'],
              )}
            >
              <div
                className={cn(
                  classes['vacancy-info__top-text'],
                )}
              >
                {`Вакансия${isMobile ? ':' : ''}`}
              </div>
              <div className={classes['vacancy-info__bot-text']}>{title}</div>
            </div>

            <div className={classes['checkboxes-area']}>
              <div
                className={cn(
                  classes['checkbox-area'],
                  classes['recommend-form__offset-item'],
                )}
              >
                <Field
                  render={({ field }: FieldProps) => (
                    <FormCheckbox
                      checked={field.value}
                      onChange={() => {
                        void setFieldValue(
                          'concentToBeRecommended',
                          !field.value,
                        );
                      }}
                      errors={
                        !!errors.concentToBeRecommended
                        && !!touched.concentToBeRecommended
                      }
                    />
                  )}
                  name="concentToBeRecommended"
                  value={values.concentToBeRecommended}
                  checked={values.concentToBeRecommended}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div className={classes['checkbox-area__label']}>
                  Друг проинформирован и согласен на рекомендацию
                </div>
              </div>

              <div
                className={cn(
                  classes['checkbox-area'],
                  classes['recommend-form__offset-checkbox-item'],
                )}
              >
                <Field
                  render={({ field }: FieldProps) => (
                    <FormCheckbox
                      checked={field.value}
                      onChange={() => {
                        void setFieldValue('checkPersonalData', !field.value);
                      }}
                      errors={
                        !!errors.checkPersonalData
                        && !!touched.checkPersonalData
                      }
                    />
                  )}
                  name="checkPersonalData"
                  value={values.checkPersonalData}
                  checked={values.checkPersonalData}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div className={classes['checkbox-area__text']}>
                  <div className={classes['checkbox-area__label']}>
                    Я даю согласие на обработку
                  </div>
                  <a
                    href={String(docs.documentPD)}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className={classes['checkbox-area__label-link']}
                  >
                    персональных данных
                  </a>
                  {' '}
                  и подтверждаю ознакомление с
                  <a
                    href={PERSONAL_DATA_POLICY_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className={classes['checkbox-area__label-link']}
                  >
                    политикой обработки ПДн
                  </a>
                </div>
              </div>

              <div
                className={cn(
                  classes['checkbox-area'],
                  classes['recommend-form__offset-checkbox-item'],
                )}
              >
                <Field
                  render={({ field }: FieldProps) => (
                    <FormCheckbox
                      checked={field.value}
                      onChange={() => {
                        void setFieldValue('checkIT', !field.value);
                      }}
                      errors={!!errors.checkIT && !!touched.checkIT}
                    />
                  )}
                  name="checkIT"
                  value={values.checkIT}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <div className={classes['checkbox-area__text']}>
                  <div className={classes['checkbox-area__label']}>
                    Я даю согласие на получение
                  </div>
                  <a
                    href={String(docs.documentIT)}
                    target="_blank"
                    rel="noreferrer"
                    download
                    className={classes['checkbox-area__label-link']}
                  >
                    информационно-развлекательных материалов
                  </a>
                </div>
              </div>
            </div>
          </CustomModal.Body>
          <CustomModal.Footer>
            <CustomModal.Button
              type="submit"
              isLoading={isLoading}
            >
              Отправить
            </CustomModal.Button>
          </CustomModal.Footer>
        </CustomModal.Root>
      </FormikProvider>
    </>
  );
}

export default memo(RecommendModal);
